import Vue from 'vue';

// 템플릿에서 i18n으로 라벨을 처리하므로, 스크립트에서는 값만 관리합니다.
interface FormField {
	value: any;
}

// 폼의 구조는 v-model과 연결된 키를 기준으로 유지합니다.
interface Form {
	username: FormField;
	apartment: FormField;
	dong: FormField;
	ho: FormField;
	phone: FormField;
	email: FormField;
	content: FormField;
}

export default Vue.extend({
	data () {
		return {
			// 참고: v-checkbox의 v-model로 사용되며, 체크 시 배열에 true가 추가되는 기존 로직을 유지합니다.
			check: ['white'],
			dialog: false,
			// data에서 하드코딩된 라벨을 모두 제거합니다.
			form: {
				username: { value: null },
				apartment: { value: null },
				dong: { value: null },
				ho: { value: null },
				phone: { value: null },
				email: { value: null },
				content: { value: null }
			} as Form
		}
	},
	methods:
		{
			/**
			 * 문의 유형(resident/admin)에 따라 유효성 검사 후 슬랙 메시지를 전송합니다.
			 * @param type 'resident' 또는 'admin'
			 */
			HANDLE_SEND_SLACK(type: 'resident' | 'admin') {
				if (!this.isPrivacyAgreed()) return;
				if (!this.areRequiredFieldsFilled(type)) return;

				const content = this.createContentMessage(type);
				this.sendSlackMessage(content);
			},

			/**
			 * 개인정보 수집 동의 여부를 확인하고, 미동의 시 번역된 토스트 메시지를 표시합니다.
			 * @returns 동의 시 true, 미동의 시 false
			 */
			isPrivacyAgreed(): boolean {
				if (this.check.length < 2) {
					this.$toasted.show(this.$t('QuestionPage.toasts.agreePrivacy') as string);
					return false;
				}
				return true;
			},

			/**
			 * 문의 유형에 따라 필수 입력 필드가 모두 채워졌는지 확인합니다.
			 * @param type 'resident' 또는 'admin'
			 * @returns 모두 채워졌을 시 true, 아닐 시 false
			 */
			areRequiredFieldsFilled(type: 'resident' | 'admin'): boolean {
				// 템플릿의 v-model을 기준으로 두 폼의 필수 필드 키는 동일합니다.
				const requiredFields: (keyof Form)[] = ['username', 'apartment', 'dong', 'ho', 'phone'];

				for (const key of requiredFields) {
					if (!this.form[key].value) { // null, undefined, "" 등 falsy 값 체크
						const fieldLabel = this.$t(`QuestionPage.form.${type}.${key}`) as string;
						this.$toasted.show(this.$t('QuestionPage.toasts.fillField', { field: fieldLabel }) as string);
						return false;
					}
				}
				return true;
			},

			/**
			 * 슬랙으로 보낼 메시지 내용을 동적으로 생성합니다.
			 * @param type 'resident' 또는 'admin'
			 * @returns 포맷팅된 슬랙 메시지 문자열
			 */
			createContentMessage(type: 'resident' | 'admin'): string {
				// 1. 메시지 제목에 들어갈 문의 유형을 번역합니다. (예: "입주민" 또는 "Resident")
				const translatedType = this.$t(`QuestionPage.slack.${type}`) as string;
				// 2. 번역된 유형을 포함하여 전체 제목을 생성합니다.
				const title = this.$t('QuestionPage.slack.title', { type: translatedType }) as string;

				const contentArr = [title];
				const fieldOrder: (keyof Form)[] = ['username', 'apartment', 'dong', 'ho', 'phone', 'email', 'content'];

				// 3. 정해진 순서에 따라 각 필드의 라벨과 값을 메시지 본문에 추가합니다.
				for (const key of fieldOrder) {
					const value = this.form[key].value || '-'; // 비어있는 선택 필드는 '-'로 표시
					// 현재 문의 유형에 맞는 필드 라벨을 번역하여 가져옵니다.
					const label = this.$t(`QuestionPage.form.${type}.${key}`) as string;
					contentArr.push(`${label}: ${value}`);
				}

				return contentArr.join('\n');
			},

			/**
			 * 모든 폼 필드를 초기 상태로 리셋합니다.
			 */
			resetFormValues() {
				Object.keys(this.form).forEach(key => {
					const typedKey = key as keyof Form;
					this.form[typedKey].value = null;
				});
				// 체크박스도 초기 상태로 리셋합니다.
				this.check = ['white'];
			},

			/**
			 * AWS Lambda를 통해 슬랙 메시지를 전송합니다.
			 * @param content 전송할 메시지 내용
			 */
			sendSlackMessage(content: string) {
				this.$aws.lambda.invoke(
					process.env.VUE_APP_AWS_LAMBDA_CREATE_MESSAGE_SLACK as string,
					'RequestResponse',
					JSON.stringify({
						action: 'OFFICIAL',
						content: content
					})
				).then( res => {
					this.resetFormValues();
					this.dialog = true;
				});
			},
			getPrivacyLink(type: 'resident' | 'admin') {
				return {
					name: 'contact-privacy',
					query: { type }
				};
			},
			openPrivacy(type: 'resident' | 'admin') {
				const route = this.$router.resolve({
					name: 'contact-privacy',
					query: { type }
				});
				window.open(route.href, '_blank');
			}
		},
	computed:
		{
			usePrivacy() {
				return { name: 'contact-privacy' };
			}
		}
});